import React from "react";
import {
  Dialog,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Snackbar,
  Alert,
  Typography,
  Tooltip,
  Box,
  Divider,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import LinkIcon from "@mui/icons-material/Link";
import CloseIcon from "@mui/icons-material/Close";
import Loader from "../../../common/loader";
import { employeeInvitationLink, sendRegisterInvitationEmployee } from "../../../services/employee.service";
import {ROLES} from "../../../constants/general.constant";

interface Props {
  open: boolean;
  onClose: () => void;
  user?: any;
}

const InviteEmployeeDialog: React.FC<Props> = ({ open, onClose, user }) => {
  const [email, setEmail] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [token, setToken] = React.useState<string | null>(null);
  const [inviteURL, setInviteURL] = React.useState("");
  const [businessSlug, setBusinessSlug] = React.useState<string | null>(null);
  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: "",
    type: "success",
  });

  const handleSendInvite = async () => {
    if (!email) {
      setSnackbar({
        open: true,
        message: "Please enter a valid email.",
        type: "error",
      });
      return;
    }
    await sendRegisterInvitationEmployee({ email });
    setSnackbar({
      open: true,
      message: "Invite sent successfully!",
      type: "success",
    });
    setEmail("");
    onClose();
  };

  const handleCopyURL = async () => {
    await navigator.clipboard.writeText(inviteURL);
    setSnackbar({ open: true, message: "Invite URL copied!", type: "success" });
  };

  React.useEffect(() => {
    if (open && user) {
      const fetchLink = async () => {
        setLoading(true);
        try {
          const businessId = user.role === ROLES.CO_OWNER ? user.businessId : user.id;
          const response = await employeeInvitationLink(businessId);
          if (response.token) {
            setBusinessSlug(response.businessSlug);
            setToken(response.token);
          }
        } catch (error: any) {
          setSnackbar({ open: true, message: error?.message || "Failed to generate link.", type: "error" });
        } finally {
          setLoading(false);
        }
      };
      fetchLink();
    }
  }, [open, user]);

  React.useEffect(() => {
    if (token || businessSlug) {
      setInviteURL(`${process.env.REACT_APP_URL}/register-employee?token=${token}`);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, businessSlug]);

  if (loading) return <Loader />;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <Box className="relative px-5 pt-5 pb-2">
          <Box className="flex flex-col gap-4">
            <Box className="flex items-center justify-between gap-3">
              <Box className="flex items-center gap-3">
                <div>
                  <PersonAddAlt1Icon className="mx-1" fontSize="large" />
                </div>
                <div>
                  <Typography variant="h6" className="font-semibold text-gray-800">
                    Invite an Employee
                  </Typography>
                  <Typography variant="body2" className="text-gray-500">
                    Send an invitation via email or share the link.
                  </Typography>
                </div>
              </Box>
              <IconButton
                onClick={onClose}
                aria-label="close"
                sx={{ color: (theme) => theme.palette.grey[500] }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            <Divider className="my-5" />

            <Alert
              severity="warning"
              variant="outlined"
              className="mt-2"
              sx={{
                fontSize: "0.85rem",
                paddingY: 1,
                paddingX: 2,
                borderRadius: 2,
                backgroundColor: "#fff7e6",
                color: "#8a6d3b",
                borderColor: "#faebcc",
              }}
            >
              The invitation link will expire in 48 hours.
            </Alert>

            <TextField
              label="Employee Email"
              placeholder="employee@example.com"
              type="email"
              fullWidth
              variant="outlined"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              InputProps={{
                style: { borderRadius: 10 },
              }}
            />

            <div className="flex flex-col gap-2">
              <Typography className="text-sm font-medium text-gray-600">
                Invitation Link
              </Typography>
              {!token ? (
                <Typography className="text-sm font-medium text-red-600">
                  Invitation Link failed to generate. Please try again.
                </Typography>
              ) : (
                <div className="bg-gray-50 border border-gray-200 rounded-[10px] px-3 py-2 flex items-center justify-between">
                  <Box className="flex items-center gap-2 text-sm text-gray-700 break-all">
                    <LinkIcon fontSize="small" />
                    <span>{inviteURL}</span>
                  </Box>
                  <Tooltip title="Copy to clipboard">
                    <IconButton onClick={handleCopyURL} size="small">
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </div>
              )}
            </div>
          </Box>

          <DialogActions className="px-0 pb-0 flex justify-end mt-3">
            <Button
              onClick={onClose}
              startIcon={<CloseIcon />}
              variant="outlined"
              color="error"
              sx={{ borderRadius: 2, fontWeight: 600 }}
            >
              Close
            </Button>
            <Button
              onClick={handleSendInvite}
              startIcon={<EmailIcon />}
              variant="contained"
              className="bg-blue-600 hover:bg-blue-700 text-white"
              sx={{ borderRadius: 2, fontWeight: 600 }}
            >
              Send Invite
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.type as any}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default InviteEmployeeDialog;
